import type z from 'zod';
import type { ItemCategoryRecommendationsSchema } from '../../schemas/items';
import { getTreeFromFlatData } from '../tree/tree-data-utils';
import type { NodeBase } from '../../entities/Node';
import { createId } from '../tree/treeNode';
import { NODE_TYPE_CATEGORY_GROUP, NODE_TYPE_ITEM } from '../types/nodeTypes';
import { formatItemNo } from '../data/utils';

type ItemCategoryRecommendations = z.infer<typeof ItemCategoryRecommendationsSchema>;
type CategoryGroupMap = Map<
  string,
  {
    globalCategoryName: string;
    items: ItemCategoryRecommendations;
  }
>;

type CategoryGroupNode = {
  title: string;
  catalogId: string;
  key: string;
  allowChildItems: boolean;
  level: number;
  isLeaf: boolean;
  type: string;
  parentId: string;
  role: string;
  id: string;
};

type RecommendationItemNode = ReturnType<typeof createRecommendationItemNode>;
type FlatDataNode = CategoryGroupNode | RecommendationItemNode;

export const createItemsCategoryRecommendationsNodes = ({
  catalogId,
  items,
}: {
  catalogId: string;
  items: ItemCategoryRecommendations;
}) => {
  const categoryGroups = createCategoryGroups(items);
  const flatData: FlatDataNode[] = [];

  // Sort categories alphabetically
  Array.from(categoryGroups.entries())
    .sort(([catNameA], [catNameB]) => catNameA.localeCompare(catNameB))
    .forEach(([categoryName, { items: groupItems }]) => {
      // Category group node
      const categoryGroupNode: CategoryGroupNode = toCategoryGroupNode({
        catalogId,
        childItemCount: groupItems.length,
        categoryName,
      });
      flatData.push(categoryGroupNode);

      // Sort items by itemNo
      const sortedItems = [...groupItems].sort((a, b) =>
        a.itemNo.localeCompare(b.itemNo),
      );

      // Add items under category group
      flatData.push(
        ...sortedItems.map((item) =>
          createRecommendationItemNode(catalogId, categoryGroupNode.id, item),
        ),
      );
    });

  // @ts-expect-error getTreeFromFlatData not typed
  return getTreeFromFlatData({ flatData }) as NodeBase[];
};

function toCategoryGroupNode({
  catalogId,
  childItemCount,
  categoryName,
}: {
  catalogId: string;
  childItemCount: number;
  categoryName: string;
}) {
  const id = createId({
    contentId: categoryName,
    type: NODE_TYPE_CATEGORY_GROUP,
    parentId: '',
  });

  return {
    title: categoryName,
    catalogId,
    key: id,
    allowChildItems: true,
    level: 0,
    isLeaf: false,
    type: NODE_TYPE_CATEGORY_GROUP,
    group: { childItemCount },
    parentId: '0',
    role: 'treeitem',
    id,
  };
}

function createRecommendationItemNode(
  catalogId: string,
  parentId: string,
  item: ItemCategoryRecommendations[number],
) {
  const countrySpecificItemCount = item.countrySpecificItemCount ?? 0;
  const id = createId({
    contentId: item.itemNo,
    type: NODE_TYPE_ITEM,
    parentId,
  });

  return {
    id,
    parentId,
    catalogId,
    key: id,
    title: item?.itemName || '',
    itemNoFormated: formatItemNo(item.itemNo),
    itemNo: item.itemNo,
    itemType: item.itemType,
    type: NODE_TYPE_ITEM,
    isLeaf: countrySpecificItemCount === 0,
    level: 1,
    role: 'treeitem',
    isChildItem: true,
    children: [],
    expanded: false,
    item: {
      itemNo: item.itemNo,
      itemCategoryRecommendationId: item.itemCategoryRecommendationId,
      categoryId: item.categoryId,
      externalCategoryId: item.externalCategoryId,
      globalCategoryName: item.globalCategoryName,
      reviewStatus: item.reviewStatus,
      itemName: item.itemName,
      itemType: item.itemType,
      catalogueId: item.catalogueId,
      countrySpecificItemCount,
      recommendationCategories: [
        {
          externalCategoryId: item.externalCategoryId,
          categoryId: item.categoryId,
          globalCategoryName: item.globalCategoryName,
          itemCategoryRecommendationId: item.itemCategoryRecommendationId,
        },
      ],
    },
    isItemCategoryRecommendation: true,
  };
}

function createCategoryGroups(items: ItemCategoryRecommendations) {
  return items.reduce<CategoryGroupMap>((mapped, item) => {
    const categoryName = item.globalCategoryName;
    if (!mapped.has(categoryName)) {
      mapped.set(categoryName, {
        globalCategoryName: categoryName,
        items: [],
      });
    }
    mapped.get(categoryName)!.items.push(item);
    return mapped;
  }, new Map());
}
